import React, { memo, useRef, useState, useEffect } from "react";

import {
  TERipple,
  TEModal,
  TEModalDialog,
  TEModalContent,
  TEModalHeader,
  TEModalBody,
  TEModalFooter,
} from "tw-elements-react";
import emailjs from "@emailjs/browser";
import { Typography, Button } from "@material-tailwind/react";
// hook
import { useTranslation, Trans } from "react-i18next";
import PremaritalCounselingTestimonial from "../testimonials/PremaritalCounselingTestimonial";
import ContactForm from "../ContactForm";
import { z } from "zod";
// Define the Zod schema for form validation
const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email address"),
  subject: z.string().min(1, "Subject is required"),
  message: z.string().min(1, "Message is required"),
});

const PremaritalCounseling = memo(({ open, close, setOpen }) => {
  const { t, i18n } = useTranslation();
  const service_id = process.env.REACT_APP_EMAIL_SERVICE_ID;
  const template_id = process.env.REACT_APP_EMAIL_TEMPLATE_ID;
  const public_key = process.env.REACT_APP_EMAIL_PUBLIC_KEY;
  // Define the form reference and state
  const form = useRef();
  const [showModal, setShowModal] = useState(false);
  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    subject:
      i18n.resolvedLanguage === "zh"
        ? "婚前/婚後輔導查詢"
        : "Premarital or Postmarital Counseling Inquiry",
    message:
      i18n.resolvedLanguage === "zh"
        ? "你好，黃姑娘我想查詢有關婚前/婚後輔導的價錢和時間。我的聯絡號碼是：8888 - 8888。謝謝！"
        : "Hello, Miss Wong. I would like to inquire about the prices and schedule for Premarital or Postmarital Counseling. My contact number is: 8888 - 8888. Thank you!",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormValues((prevValues) => ({
      ...prevValues,
      subject:
        i18n.resolvedLanguage === "zh"
          ? "婚前/婚後輔導查詢"
          : "Premarital or Postmarital Counseling Inquiry",
      message:
        i18n.resolvedLanguage === "zh"
          ? "你好，黃姑娘我想查詢有關婚前/婚後輔導的價錢和時間。我的聯絡號碼是：8888 - 8888。謝謝！"
          : "Hello, Miss Wong. I would like to inquire about the prices and schedule for Premarital or Postmarital Counseling. My contact number is: 8888 - 8888. Thank you!",
    }));
  }, [i18n.resolvedLanguage]);

  // Define the sendEmail function
  const sendEmail = (e) => {
    e.preventDefault();
    console.log("counseling formValues", formValues);

    // Validate form values using Zod schema
    const validation = formSchema.safeParse(formValues);

    if (!validation.success) {
      // Handle validation errors
      console.log("Validation errors:", validation.error.format());
      setErrors(validation.error.format()); // Update errors state
      return;
    }
    emailjs.sendForm(service_id, template_id, form.current, public_key).then(
      (result) => {
        // console.log(result.text);
        setShowModal(true);
        setFormValues({
          name: "",
          email: "",
          subject: "",
          message: "",
        });
      },
      (error) => {
        console.log(error.text);
      }
    );
    setErrors({}); // Clear errors if validation passes
    // Proceed with sending email
  };
  return (
    <>
      <TEModal show={open} setShow={setOpen} scrollable>
        <TEModalDialog size="xl">
          <TEModalContent>
            <TEModalHeader>
              {/* <!--Modal title--> */}
              <h5
                className={` leading-normal text-neutral-800 dark:text-neutral-200
             ${
               i18n.resolvedLanguage === "zh"
                 ? "font-reborn ml-3 text-2xl"
                 : "text-xl font-medium"
             }
              `}
              >
                {t("popUp.premarital-counseling.title")}
              </h5>
              {/* <!--Close button--> */}
              <button
                type="button"
                className="box-content rounded-none border-none hover:no-underline hover:opacity-75 focus:opacity-100 focus:shadow-none focus:outline-none"
                onClick={() => close(false)}
                aria-label="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="h6 w-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </TEModalHeader>

            <TEModalBody
              className={`${
                i18n.resolvedLanguage === "zh" ? "font-reborn text-lg" : ""
              }`}
            >
              <div className="popContainer">
                {/* Video Section */}
                <div className="mb-6 rounded-lg overflow-hidden shadow-md">
                  <div
                    className="relative overflow-hidden"
                    style={{ paddingTop: "56.25%", width: "100%" }}
                  >
                    <iframe
                      className="absolute inset-0 w-full h-full"
                      src="https://www.youtube.com/embed/ozEUijIYDQs?si=fvjKzkFvaz-MpuN0"
                      title="Premarital Counseling Video"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </div>

                {/* Main Description */}
                <div className="mb-6 bg-gray-50 p-5 rounded-lg border border-gray-200">
                  <p className="leading-relaxed text-gray-700">
                    <Trans i18nKey="popUp.premarital-counseling.p1">
                      Using <strong>"Prepare/Enrich Inventory"</strong>, which
                      is the premarital or marital relationship assessment tool,
                      as a foundation to prepare both parties ready to move
                      towards the next stage of life together or continue to
                      carry on the marriage
                    </Trans>
                  </p>
                </div>

                {/* Areas We Can Help */}
                <div className="mb-6 bg-pink-50 p-5 rounded-lg border border-pink-200">
                  <h3
                    className={`font-semibold mb-4 text-pink-900 ${
                      i18n.resolvedLanguage === "zh" ? "text-xl" : "text-lg"
                    }`}
                  >
                    {t("popUp.premarital-counseling.deal-with")}
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-pink-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <p className="text-gray-700 leading-relaxed">
                        {t("popUp.premarital-counseling.1")}
                      </p>
                    </div>
                    <div className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-pink-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <p className="text-gray-700 leading-relaxed">
                        {t("popUp.premarital-counseling.2")}
                      </p>
                    </div>
                    <div className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-pink-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <p className="text-gray-700 leading-relaxed">
                        {t("popUp.premarital-counseling.3")}
                      </p>
                    </div>
                    <div className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-pink-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <p className="text-gray-700 leading-relaxed">
                        {t("popUp.premarital-counseling.4")}
                      </p>
                    </div>
                    <div className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-pink-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <p className="text-gray-700 leading-relaxed">
                        {t("popUp.premarital-counseling.5")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Service Details Section */}
                <div className="mb-6 bg-rose-50 p-5 rounded-lg border border-rose-200">
                  <h3
                    className={`font-semibold mb-4 text-rose-900 ${
                      i18n.resolvedLanguage === "zh" ? "text-xl" : "text-lg"
                    }`}
                  >
                    {i18n.resolvedLanguage === "zh"
                      ? "服務詳情"
                      : "Service Details"}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-rose-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <p className="text-gray-700 leading-relaxed">
                        {t("popUp.premarital-counseling.7")}
                      </p>
                    </div>
                    <div className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-rose-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <p className="text-gray-700 leading-relaxed">
                        {t("popUp.premarital-counseling.8")}
                      </p>
                    </div>
                    <div className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-rose-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <p className="text-gray-700 leading-relaxed">
                        {t("popUp.premarital-counseling.9")}
                      </p>
                    </div>
                    <div className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-rose-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <div className="text-gray-700 leading-relaxed">
                        <p>{t("popUp.premarital-counseling.11")}</p>
                        <p>{t("popUp.premarital-counseling.12")}</p>
                        <div className="ml-6 mt-2 space-y-1">
                          <p>{t("popUp.premarital-counseling.13")}</p>
                          <p>{t("popUp.premarital-counseling.14")}</p>
                          <p>{t("popUp.premarital-counseling.15")}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-rose-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <p className="text-gray-700 leading-relaxed">
                        {t("popUp.premarital-counseling.10")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Testimonials */}
                <PremaritalCounselingTestimonial />

                {/* Contact Form Section */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  {/* <h3 className={`font-semibold mb-4 text-gray-900 ${
                    i18n.resolvedLanguage === "zh" ? "text-xl" : "text-lg"
                  }`}>
                    {i18n.resolvedLanguage === "zh" ? "立即查詢" : "Contact Us"}
                  </h3> */}
                  <ContactForm
                    form={form}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    sendEmail={sendEmail}
                    errors={errors}
                  />
                </div>
              </div>
            </TEModalBody>
            <TEModalFooter
              className={`   ${
                i18n.resolvedLanguage === "zh" ? "font-reborn text-lg" : ""
              }`}
            >
              <TERipple rippleColor="light" className="mr-2">
                <Button
                  variant="outlined"
                  size="sm"
                  className="border px-4 rounded font-reborn"
                  onClick={sendEmail}
                >
                  {i18n.resolvedLanguage === "zh" ? (
                    <Typography className="text-gray-600 font-reborn text-sm">
                      {t("contact.send-message")}
                    </Typography>
                  ) : (
                    t("contact.send-message")
                  )}
                </Button>
              </TERipple>
              <TERipple rippleColor="light" className="mr-2">
                <Button
                  variant="outlined"
                  size="sm"
                  className="border px-4 rounded font-reborn"
                  onClick={() => close(false)}
                >
                  {i18n.resolvedLanguage === "zh" ? (
                    <Typography className="text-gray-600 font-reborn text-sm">
                      {t("popUp.counseling.close")}
                    </Typography>
                  ) : (
                    t("popUp.counseling.close")
                  )}
                </Button>
              </TERipple>
            </TEModalFooter>
          </TEModalContent>
        </TEModalDialog>
      </TEModal>
      {/* <!-- Modal --> */}
      <TEModal key="email_modal" show={showModal} setShow={setShowModal}>
        <TEModalDialog>
          <TEModalContent>
            <TEModalHeader>
              {/* <!--Modal title--> */}
              <h5 className="text-xl font-medium leading-normal text-neutral-800 dark:text-neutral-200">
                {t("contact.message")}
              </h5>
            </TEModalHeader>
            {/* <!--Modal body--> */}
            <TEModalBody>
              {/* Thank you for reaching out. Your email has been successfully sent. */}
              {t("contact.success")}
            </TEModalBody>
            <TEModalFooter>
              <TERipple rippleColor="light">
                <Button
                  variant="outlined"
                  size="sm"
                  className="border px-4 rounded font-reborn"
                  onClick={() => setShowModal(false)}
                >
                  {i18n.resolvedLanguage === "zh" ? (
                    <Typography className="text-gray-600 font-reborn text-sm">
                      {t("contact.close")}
                    </Typography>
                  ) : (
                    t("contact.close")
                  )}
                </Button>
              </TERipple>
            </TEModalFooter>
          </TEModalContent>
        </TEModalDialog>
      </TEModal>
    </>
  );
});

export default PremaritalCounseling;
